import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  endAt,
  Firestore,
  getDoc,
  getDocs,
  increment, limit,
  orderBy,
  query,
  Query, QuerySnapshot,
  startAt,
  updateDoc,
  where,
  writeBatch
} from '@angular/fire/firestore';
import { ShipmentGroup } from '../models/shipment-group.interface';
import { ShipmentParcel } from '../models/shipment-parcel.model';
import { Shipment } from '../models/shipment.model';
import { ShipmentStateEnum } from '../enums/shipment-state.enum';
import { TransporterEnum } from '../enums/transporter.enum';
import { ClosingDay } from '../models/closing-day.model';
import { ExpressTypeEnum } from '../enums/express-type.enum';
import { IncotermEnum } from '../enums/incoterm.enum';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Product } from '../../admin/transport-calculator/transport-calculator.component';
import { Address } from '../models/address.model';

type ParcelForm = {
  from: string;
  to: string;
  weight: number;
  length: number;
  height: number;
  width: number;
  isPallet: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ShipmentService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);

  /**
   * Subscribe to shipments with state to ship and with not empty shipment key
   * @param siteId SiteId
   * @returns The query observable
   */
  public shipmentsInProgress(siteId: string): Query<Shipment> {
    return query(collection(this.firestore, 'shipments'),
      where('shipmentKey', '!=', ''),
      where('state', '==', ShipmentStateEnum.TO_SHIP),
      where('siteId', '==', siteId)
    )
      .withConverter(Shipment.converter);
  }

  /**
   * Get the shipment groups shipped between two dates
   * @param siteId SiteId
   * @param startDate The start date (inclusive)
   * @param endDate The end date (inclusive)
   * @returns The query observable
   */
  public shipmentGroupsBetween(siteId: string, startDate: string, endDate: string): Query<Shipment> {
    return query(collection(this.firestore, 'shipments'),
      where('siteId', '==', siteId),
      orderBy('shipDate', 'desc'),
      startAt(endDate),
      endAt(startDate))
      .withConverter(Shipment.converter);
  }

  /**
   * Update a shipment group from its shipments updates
   * @param snapshot The query snapshot
   * @param groups The group to update
   */
  updateShipments(snapshot: QuerySnapshot<Shipment>, groups: ShipmentGroup[]): void {
    for (const changes of snapshot.docChanges()) {
      switch (changes.type) {
      case 'added': {
        const added = changes.doc.data();
        let group = groups.find(g => g.shipmentKey === added.shipmentKey)
        if (!group) {
          group = this.buildGroupFromShipment(changes.doc.data());
          group.shipments.push(changes.doc.data());
          groups.push(group);
        } else {
          group.shipments.push(changes.doc.data());
        }
        break;
      }
      case 'removed': {
        const removed = changes.doc.data();
        const index = groups.findIndex(g => g.shipmentKey === removed.shipmentKey);
        const group = groups[index];
        const shipmentIndex = group.shipments.findIndex(s => s.id === removed.id);
        if (shipmentIndex !== -1) {
          group.shipments.splice(shipmentIndex, 1);
        }
        if (!group.shipments.length && index !== -1) {
          groups.splice(index, 1);
        }
        break;
      }
      case 'modified': {
        const modified = changes.doc.data();
        const index = groups.findIndex(g => g.shipmentKey === modified.shipmentKey);
        const group = groups[index];
        const shipmentIndex = group.shipments.findIndex(s => s.id === modified.id);
        if (shipmentIndex === -1) {
          /** Remove from old group */
          for (const sGroup of groups) {
            const shipment = sGroup.shipments.find(s => s.id === modified.id);
            if (!shipment) {
              continue;
            }
            sGroup.shipments = sGroup.shipments.filter(s => s.id !== shipment.id);
            if (!sGroup.shipments.length) {
              const sIndex = groups.findIndex(g => g.shipmentKey === sGroup.shipmentKey);
              groups.splice(sIndex, 1);
            }
          }
          /** Append to new group */
          group.shipments.push(changes.doc.data());
          break;
        }
        Object.assign(group.shipments[shipmentIndex], modified);
        break;
      }
      }
    }
    for (const group of groups) {
      const earliestDeliveryDate = Math.min(...group.shipments.map(s => s.deliveryDate.getTime()))
      group.earliestDeliveryDate = new Date(earliestDeliveryDate);
    }
  }

  /**
   * Get all shipments for a orderId, does not retrieve the parcels
   * @param orderId The orderId
   * @returns Shipment Query
   */
  public queryShipmentsByOrderId(orderId: string): Query<Shipment> {
    return query(collection(this.firestore, 'shipments'),
      where('orderId', '==', orderId))
      .withConverter(Shipment.converter);
  }

  /**
   * Get all shipments for a orderId, does not retrieve the parcels
   * @param orderId The orderId
   * @returns Shipment Query
   */
  public async getByOrderId(orderId: string): Promise<Shipment[]> {
    const snapshot = await getDocs(this.queryShipmentsByOrderId(orderId));
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Get all shipments for a Multipress job number within one organization, does not retrieve the parcels
   * @param transactionId The order transaction id
   * @param organizationId The owning organization id
   * @returns Matching shipments
   */
  public async getByTransactionId(transactionId: string, organizationId: string): Promise<Shipment[]> {
    if (!transactionId || !organizationId) {
      return [];
    }
    const snapshot = await getDocs(query(collection(this.firestore, 'shipments'),
      where('organizationId', '==', organizationId),
      where('transactionId', '==', transactionId))
      .withConverter(Shipment.converter));
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Get the shipment groups for a closing of day
   * @param transporter transporter
   * @param site site
   * @returns The shipments available for closing
   */
  public shipmentGroupsForClosingOfDay(transporter: TransporterEnum, site: string): Promise<ShipmentGroup[]> {
    const q = query(collection(this.firestore, 'shipments'),
      where('transporterId', '==', transporter),
      where('siteId', '==', site),
      where('state', '==', ShipmentStateEnum.PLANNED))
      .withConverter(Shipment.converter);
    return getDocs(q)
      .then(snap => {
        if (snap.empty) {
          return [];
        }
        const shipments = snap.docs.map(doc => doc.data());
        return this.groupShipments(shipments);
      });
  }

  /**
   * Synchronise a shipment by transaction id
   * @param organizationId The organization id
   * @param transactionId The order transaction id
   * @returns The error code
   */
  public async synchronise(organizationId: string, transactionId: string): Promise<string> {
    return httpsCallable<{ transactionId: string, organizationId: string },
      { reason: string }>(this.functions, 'synchronise_shipment_gen2')({ transactionId, organizationId })
      .then(res => res.data.reason);
  }

  /**
   * Get a shipment group from shipment key, does not retrieve the parcels
   * @param shipmentKey The shipment key
   * @returns ShipmentGroup
   */
  public getGroupByShipmentKey(shipmentKey: string): Promise<ShipmentGroup> {
    const q = query(collection(this.firestore, 'shipments'),
      where('shipmentKey', '==', shipmentKey))
      .withConverter(Shipment.converter);
    return getDocs(q)
      .then(docSnapshots => {
        if (docSnapshots.empty) {
          return null;
        }
        const shipments = docSnapshots.docs.map(doc => doc.data());
        return this.groupShipments(shipments)[0];
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Subscribe unprocessed shipments (state to ship)
   * @param siteId SiteId
   * @returns Shipment query
   */
  public unprocessedShipments(siteId: string): Query<Shipment> {
    return query(collection(this.firestore, 'shipments'),
      where('state', '==', ShipmentStateEnum.TO_SHIP),
      where('siteId', '==', siteId),
    )
      .withConverter(Shipment.converter);
  }

  /**
   * Retrieve all shipments which could be delivered with the selected shipment
   * @param group The selected shipment group
   * @returns The list of compliant shipments for the delivery
   */
  public getShipmentSuggestion(group: ShipmentGroup): Query<Shipment> {
    return query(collection(this.firestore, 'shipments'),
      where('shipmentKey', '!=', group.shipmentKey),
      where('shipTo.zip', '==', group.shipTo.zip),
      where('siteId', '==', group.siteId),
      where('charter', '==', group.charter),
      where('pickUp', '==', group.pickUp),
      where('courier', '==', group.courier),
      where('state', '==', ShipmentStateEnum.TO_SHIP))
      .withConverter(Shipment.converter);
  }

  /**
   * Get parcels by shipment key sorted by index
   * @param shipmentKey The shipment key
   * @returns The parcels
   */
  public getParcels(shipmentKey: string): Promise<ShipmentParcel[]> {
    const q = this.getParcelsQuery(shipmentKey);
    return getDocs(q)
      .then(docSnapshots => {
        if (docSnapshots.empty) {
          return [];
        }
        return docSnapshots.docs
          .map(doc => doc.data())
          .sort((a, b) => a.index - b.index);
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Get parcels query by shipment key sorted by index
   * @param shipmentKey The shipment key
   * @returns Parcel query
   */
  public getParcelsQuery(shipmentKey: string): Query<ShipmentParcel> {
    return query(collection(this.firestore, 'parcels'),
      where('shipmentKey', '==', shipmentKey), orderBy('index', 'asc'))
      .withConverter(ShipmentParcel.converter);
  }

  /**
   * Save parcel index
   * @param id the parcel id
   * @param index the desired index
   * @returns Promise
   */
  public saveParcelIndex(id: string, index: number): Promise<void> {
    const ref = doc(this.firestore, 'parcels', id).withConverter(ShipmentParcel.converter);
    return updateDoc(ref, { index });
  }

  /**
   * Save dimension, weight, pallet and content
   * @param parcel The parcel
   * @returns Promise
   */
  public saveParcelDimensionAndContent(parcel: Partial<ShipmentParcel>): Promise<void> {
    const ref = doc(this.firestore, 'parcels', parcel.id).withConverter(ShipmentParcel.converter);
    return updateDoc(ref, {
      isPallet: parcel.isPallet,
      length: parcel.length,
      width: parcel.width,
      height: parcel.height,
      weight: parcel.weight,
      content: parcel.content?.map(content => ({ quantity: content.quantity, shipmentId: content.shipmentId })) ?? []
    });
  }

  /**
   * Save dimension, weight, pallet and content
   * @param parcel The parcel
   * @returns Promise
   */
  public updateParcelContent(parcel: Partial<ShipmentParcel>): Promise<void> {
    const ref = doc(this.firestore, 'parcels', parcel.id).withConverter(ShipmentParcel.converter);
    return updateDoc(ref, {
      content: parcel.content?.map(content => ({ quantity: content.quantity, shipmentId: content.shipmentId }))
    });
  }

  /**
   * Subscribe to shipments
   * @param parcel The parcel to create
   * @returns The created parcel
   */
  public createParcel(parcel: ShipmentParcel): Promise<ShipmentParcel> {
    const ref = collection(this.firestore, 'parcels').withConverter(ShipmentParcel.converter);
    return addDoc(ref, parcel)
      .then(doc => {
        parcel.id = doc.id;
        return parcel;
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Remove a parcel
   * @param parcelId parcelId
   * @returns Promise
   */
  public async deleteParcel(parcelId: string): Promise<void> {
    const ref = doc(this.firestore, 'parcels', parcelId).withConverter(ShipmentParcel.converter);
    return deleteDoc(ref);
  }

  /**
   * Create a shipment
   * @param shipmentGroup shipmentGroup
   * @param parcels parcels
   * @param transporter transporter
   * @param express express
   * @param incoterm incoterm
   * @param operator operator
   * @returns ShipmentGroup
   */
  public async createShipments(shipmentGroup: ShipmentGroup, parcels: ShipmentParcel[], transporter: TransporterEnum,
    express: ExpressTypeEnum, incoterm: IncotermEnum, operator: string): Promise<ShipmentGroup> {
    const payload: any = { ...shipmentGroup, parcels };
    return httpsCallable<{
      shipmentGroup: ShipmentGroup,
      transporter: TransporterEnum,
      express: ExpressTypeEnum,
      incoterm: IncotermEnum,
      operator: string }, { shipmentGroup: ShipmentGroup }>
      (this.functions, 'plan_shipment_gen2')({ shipmentGroup: payload, transporter, express, incoterm, operator })
      .then(result => {
        this._restoreShipmentDates(result.data.shipmentGroup);
        return result.data.shipmentGroup;
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Delete the shipment group
   * @param shipmentKey shipmentKey
   * @returns Promise
   */
  public async deleteShipmentGroup(shipmentKey: string): Promise<void> {
    await httpsCallable<{ shipmentKey: string }, void>(this.functions, 'archive_shipment_gen2')({ shipmentKey });
  }

  /**
   * Validate shipments
   * @param data data
   * @param data.transporter data.transporter
   * @param data.site data.site
   * @param data.shipmentKeys data.shipmentKeys
   * @param data.user data.user
   * @param data.signature data.signature
   * @returns The html document of the closing of day
   */
  public async validateTransporter(data: {
    transporter: TransporterEnum,
    site: string,
    shipmentKeys: string[],
    user: string,
    signature: string
  }): Promise<{ documentHtml: string }> {
    return httpsCallable<{ transporter: TransporterEnum, site: string, shipmentKeys: string[], user: string,
      signature: string },
      { documentHtml: string, documentPath: string }>(this.functions, 'validate_shipments_gen2')(data)
      .then(result => result.data);
  }

  /**
   * Export last month parcels
   * @param shipmentKeys shipmentKeys
   * @returns The parcels data
   */
  public async extractParcelsData(shipmentKeys: string[]): Promise<{
    width: number,
    length: number,
    height: number,
    weight: number,
    shipmentKey: string
  }[]> {
    try {
      const promises: Promise<ShipmentParcel[]>[] = [];
      const chunkSize = 10;
      for (let i = 0; i < shipmentKeys.length; i += chunkSize) {
        const chunk = shipmentKeys.slice(i, i + chunkSize);
        const q = query(collection(this.firestore, 'parcels'),
          where('shipmentKey', 'in', chunk))
          .withConverter(ShipmentParcel.converter);
        promises.push(getDocs(q).then(snapshot => snapshot.docs.map(doc => doc.data())))
      }
      const parcelsChunks = await Promise.all(promises);
      const parcels = parcelsChunks
        .flat()
        .map(parcel => ({
          width: parcel.width,
          height: parcel.height,
          length: parcel.length,
          weight: parcel.weight,
          shipmentKey: parcel.shipmentKey,
        }));
      parcels.sort((a, b) => a.shipmentKey.localeCompare(b.shipmentKey, undefined, {
        numeric: true,
        sensitivity: 'base'
      }));
      return parcels;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  /**
   * Get updated data of the shipment and check the shipment validity
   * @param shipmentId shipmentId
   * @returns The updated data of the shipment and validity
   */
  public async verifyShipment(shipmentId: string): Promise<{ code: string, shipment: Shipment }> {
    return httpsCallable<{ id: string },
      { code: string, shipment: Shipment }>(this.functions, 'get_shipment_gen2')({ id: shipmentId })
      .then(res => res.data);
  }

  /**
   * Cancel a shipment
   * @param shipmentGroup shipmentGroup
   * @param parcels parcels
   * @returns shipmentGroup
   */
  public async cancelShipments(shipmentGroup: ShipmentGroup, parcels: ShipmentParcel[]): Promise<ShipmentGroup> {
    const payload: any = { ...shipmentGroup };
    payload.parcels = parcels;
    return httpsCallable<{ payload: any },
      { shipmentGroup: ShipmentGroup }>(this.functions, 'cancel_shipment_gen2')(payload)
      .then(result => {
        this._restoreShipmentDates(result.data.shipmentGroup);
        return result.data.shipmentGroup;
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Print a shipment label
   * @param shipmentKey shipmentKey
   * @param options options
   * @param options.splitByFormat options.splitByFormat
   * @returns Html of the label
   */
  public async printShipment(shipmentKey: string, options: { splitByFormat: boolean }): Promise<string> {
    return httpsCallable<{ shipmentKey: string, options: { splitByFormat: boolean }},
      { html: string }>(this.functions, 'print_shipment_gen2')({ shipmentKey, options })
      .then(result => result.data.html)
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Update shipment key
   * @param shipment shipment
   * @returns Promise
   */
  public updateShipmentKey(shipment: Shipment): Promise<void> {
    const ref = doc(this.firestore, 'shipments', shipment.id).withConverter(Shipment.converter);
    return updateDoc(ref, { shipmentKey: shipment.shipmentKey });
  }

  /**
   * Update shipment key
   * @param id shipment id
   * @param shipment shipment data
   * @returns Promise
   */
  public update(id: string, shipment: Partial<Shipment>): Promise<void> {
    const ref = doc(this.firestore, 'shipments', id)
      .withConverter(Shipment.converter);
    return updateDoc(ref, shipment);
  }

  /**
   * Generate a new shipment key
   * @returns Shipment key
   */
  public createShipmentKey(): Promise<string> {
    const ref = doc(this.firestore, 'indexes', 'shipments');
    return updateDoc(ref, { shipmentIndex: increment(1) })
      .then(() => getDoc(ref))
      .then(doc => String(doc.data().shipmentIndex))
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Create a group from shipments
   * @param shipments The shipments to group
   * @returns Grouped shipment
   */
  public groupShipments(shipments: Shipment[]): ShipmentGroup[] {
    const groups: ShipmentGroup[] = Object.values(shipments.reduce((groupDict, shipment) => {
      if (!shipment.shipmentKey) {
        throw new Error('Shipment key cannot be empty');
      }
      if (!groupDict[shipment.shipmentKey]) {
        groupDict[shipment.shipmentKey] = this.buildGroupFromShipment(shipment);
      }
      groupDict[shipment.shipmentKey].shipments.push(shipment);
      return groupDict;
    }, {}));
    for (const group of groups) {
      const earliestDeliveryDate = Math.min(...group.shipments.map(s => s.deliveryDate.getTime()))
      group.earliestDeliveryDate = new Date(earliestDeliveryDate);
    }
    return groups;
  }

  /**
   * Create a shipment group from a shipment
   * @param shipment The shipment used to created group
   * @returns The shipment group
   */
  buildGroupFromShipment(shipment: Shipment): ShipmentGroup {
    return {
      shipmentKey: shipment.shipmentKey,
      organizationId: shipment.organizationId,
      shipTo: shipment.shipTo,
      shipFrom: shipment.shipFrom,
      trackingId: shipment.trackingId,
      trackingUrl: shipment.trackingUrl,
      transporterId: shipment.transporterId,
      transportCategory: shipment.transportCategory,
      overridden: shipment.overridden,
      siteId: shipment.siteId,
      shipDate: shipment.shipDate,
      shipTime: shipment.shipTime,
      departure: shipment.departure,
      state: shipment.state,
      stateText: shipment.stateText,
      lockComment: shipment.lockComment,
      lockedBy: shipment.lockedBy,
      lockerName: shipment.lockerName,
      lockDate: shipment.lockDate,
      operator: shipment.operator,
      earliestDeliveryDate: null,
      shipments: [],
      parcels: [],
      charter: shipment.charter,
      express: shipment.express,
      pickUp: shipment.pickUp,
      courier: shipment.courier,
      external: shipment.external
    };
  }

  /**
   * Update in firebase the state of all shipments in the group
   * @param shipments shipments
   * @param state state
   */
  public async updateState(shipments: Shipment[], state: ShipmentStateEnum): Promise<void> {
    const batch = writeBatch(this.firestore)
    for (const shipment of shipments) {
      const ref = doc(this.firestore, 'shipments', shipment.id).withConverter(Shipment.converter);
      batch.update(ref, { state });
      if (shipment.webShipmentId) {
        const ref = doc(this.firestore, 'web-shipments', shipment.webShipmentId);
        batch.update(ref, { state });
      }
    }
    await batch.commit();
  }

  /**
   * Move a shipment not packed yet to another shipping site, with the site pickup address as departure address
   * @param shipment The shipment to move
   * @param siteId The new site id
   * @param shipFrom The pickup address of the new site
   */
  public updateSite(shipment: Shipment, siteId: string, shipFrom: Address): Promise<void> {
    const ref = doc(this.firestore, 'shipments', shipment.id);
    return updateDoc(ref, { siteId, shipFrom: { ...shipFrom } });
  }

  /**
   * Update in firebase the comment of all shipments in the group
   * @param group group
   * @param partialShipment partialShipment
   */
  public async updateComment(group: ShipmentGroup, partialShipment: Partial<Shipment>): Promise<void> {
    for (const shipment of group.shipments) {
      const ref = doc(this.firestore, 'shipments', shipment.id)
        .withConverter(Shipment.converter);
      await updateDoc(ref, partialShipment);
      shipment.lockComment = partialShipment.lockComment;
      shipment.lockedBy = partialShipment.lockedBy;
      shipment.lockerName = partialShipment.lockerName;
      shipment.lockDate = partialShipment.lockDate;
    }
  }

  /**
   * Get the list of "closing of day" for the given date
   * @param date YYYY-MM-DD
   * @param site YYYY-MM-DD
   * @returns List of closing days
   */
  public getClosings(date: string, site: string): Promise<ClosingDay[]> {
    const q = query(
      collection(this.firestore, 'closing-day'),
      where('date', '==', date),
      where('site', '==', site)
    )
      .withConverter(ClosingDay.converter);
    return getDocs(q)
      .then(docSnapshots => {
        if (docSnapshots.empty) {
          return [];
        }
        return docSnapshots.docs.map(doc => doc.data());
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Restore the date of shipments, received as anonymized timestamp, to Date
   * @param group group
   */
  private _restoreShipmentDates(group): void {
    group.shipments.forEach(s => {
      if (s.deliveryDate) {
        s.deliveryDate = new Date(s.deliveryDate);
      }
      if (s.stateDate) {
        s.stateDate = new Date(s.stateDate);
      }
    });
  }

  /**
   * Call API to get all prices for requested delivery
   * @param parcel The data for calculation
   * @returns Product by transporter
   */
  public async calculateShipment(parcel: ParcelForm): Promise<{ [transporter: number]: Product[] }> {
    return httpsCallable<ParcelForm, { [transporter: number]: Product[] }>(this.functions, 'calculate_shipment_gen2')(parcel)
      .then(res => res.data)
      .catch(err => {
        console.error(err);
        throw new Error(err);
      });
  }

  /**
   * Calculates the shipment quotation price from the persisted quotation.
   * Destinations are resolved from quotation deliveries on the backend when requested.
   * The backend also persists `deliveryPrice` on the quotation.
   * @param organizationId - The ID of the organization for which the quotation is being calculated.
   * @param quotationId - The quotation document id.
   * @param withDeliveries - Whether to use the saved delivery addresses for zone pricing.
   * @returns A promise that resolves to the calculated shipment price.
   */
  async quotationPrice(organizationId: string, quotationId: string, withDeliveries: boolean): Promise<number> {
    return httpsCallable<{
      organizationId: string,
      quotationId: string,
      withDeliveries: boolean
    }, {price: number}>
    (this.functions, 'get_shipping_quotation_price_gen2')({ organizationId, quotationId, withDeliveries })
      .then(res => res.data.price)
      .catch(err => {
        console.error(err);
        throw new Error(err);
      });
  }

  /**
   * Get updated data of the shipment and check the shipment validity
   * @param orderId The order id
   * @param token Encoded email
   * @returns void
   */
  async subscribe(orderId: string, token: string): Promise<void> {
    return httpsCallable<{
      orderId: string,
      token: string
    }, void>(this.functions, 'subscribe_shipment_gen2')({ orderId, token })
      .then(result => result.data);
  }

  /**
   * Get an shipment by id
   * @param id The shipment id
   * @returns The shipment or null
   */
  async get(id: string): Promise<Shipment | null> {
    const ref = doc(this.firestore, 'shipments', id).withConverter(Shipment.converter);
    return getDoc(ref)
      .then(snapshot => {
        if (!snapshot.exists()) {
          return null;
        }
        return snapshot.data();
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Get shipments by campaign code
   * @param organizationId The organization id
   * @param campaignCode The campaign code
   * @returns The quotations or an empty array
   */
  async searchByCampaignCode(organizationId: string, campaignCode: string): Promise<Shipment[]> {
    const q = query(collection(this.firestore, 'shipments'),
      where('organizationId', '==', organizationId),
      where('supplementaryData', '>=', campaignCode),
      where('supplementaryData', '<=', campaignCode + '\uf8ff'), limit(10))
      .withConverter(Shipment.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return [];
        }
        return snapshot.docs.map(doc => doc.data());
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }
}
